import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog' 
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'  
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover" 
import { z } from 'zod' 
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'  
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import VirtualizedCommand from '@/components/VirtualizedCommand'
import { useEffect, useRef, useState } from 'react'
import { TypographyMuted, TypographyXS } from '@/components/ui/typography' 
import { useOnlineStatus } from '@/hooks/useOnlineStatus'   
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createResearchedProduct } from '@/api/business/marketResearch'
import { MarketResearch, MarkProductMarketResearch } from '@/types/MarketResearch'
import { ResearchedProductKeys } from '@/queries/useResearchedProductQuery' 
import { usePDVInMarketResearchQuery } from '@/queries/usepdvInMarketResearchQuery'
import AddDialog from '@/pages/MasterData/Product/components/AddDialog'
import useDisclosure from '@/hooks/useDisclosure'
import { useProductQuery } from '@/queries/useProductQuery'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
    MarketResearch: MarketResearch
}

const AddNewProductInProductMarketResearchDialog = ({ isOpen, onClose, MarketResearch }: AddDialogProps) => { 
    const queryClient = useQueryClient()  
    const currentLocation = useRef({} as GeolocationPosition);
    
    const [PDV, setPDV] = useState(0)
    const [mark, setMark] = useState<string[]>([])
    const [selectedMark, setSelectedMark] = useState<string>('')
    const [products, setProducts] = useState<MarkProductMarketResearch[]>([])
    const [selectedProduct, setSelectedProduct] = useState<string>('')
    const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] 
     
    const online = useOnlineStatus();
    const { toast } = useToast()  
    const { data, isFetching } = useProductQuery()
    const { data: DataPDV } = usePDVInMarketResearchQuery(MarketResearch.SK_PESQUISA)

    useEffect(() => {
        if(data) { 
            setMark( [...new Set(data.map((item)=> item.DD_MARCA))]) 
        } 
    }, [data]); 

    useEffect(() => {
        if(selectedMark && data) { 
            setProducts(data.filter(item => item.DD_MARCA == selectedMark))  
        } 
    }, [selectedMark]); 

    const {
        isOpen: isOpenAddProduct,
        onClose: onCloseAddProduct,
        onOpen: onOpenAddProduct
    } = useDisclosure()

    const schema = z.object({
        SK_CLIENTE: z.number().min(1, {
            message: 'A revenda é obrigatório',
        }),
        SK_PRODUTO: z.string().min(1, {
            message: 'O produto é obrigatório',
        }),
        VL_PRECO_COMPRA: z.number().min(1, {
            message: 'O valor de preço de compra é obrigatório',
        }),
        QTD_VOLUME_COMPRA: z.number().min(1, {
            message: 'A quantidade de compra é obrigatório',
        }),
        VL_PRECO_VENDA: z.number().min(1, {
            message: 'O valor de venda de compra é obrigatório',
        }),
        IMAGEM: z.any()
            .refine((files) => files instanceof FileList, {
            message: "A imagem é obrigatória",
            })
            .refine(
            (files) =>
                !files || ACCEPTED_IMAGE_TYPES.includes(files[0]?.type),
            "Formato de imagem inválido"
            )
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
       defaultValues: {  
            SK_CLIENTE: 0,
            SK_PRODUTO: '', 
            VL_PRECO_COMPRA: 0,
            QTD_VOLUME_COMPRA: 0,
            VL_PRECO_VENDA: 0,
            IMAGEM: undefined
        },
    })

    const formatCurrencyBR = (value: number | string) => {
        if (!value) return ""

        const number =
            typeof value === "string"
            ? Number(value.replace(/\D/g, "")) / 100
            : value

        return number.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        })
    }

    const parseCurrencyBR =  (value: string) => {
        return Number(value.replace(/\D/g, "")) / 100
    }

    const showLocationFailedAlert = (data: z.infer<typeof schema>) => {
        toast({
            title: 'Permissão negada pelo usuário.',
            description: "A aplicação precisa da permissão de localização para funcionamento, por favor ative a localização.",
            variant: 'destructive',
        })
        
        onSave(data)
    } 

    const onSubmit = (data: z.infer<typeof schema>) => {  
        navigator.geolocation.getCurrentPosition((position) => {
            currentLocation.current = position; 
            onSave(data)
        }, () => showLocationFailedAlert(data)); 
        form.reset();
    }

    const handleCreateNewProduct = () => { 
        setSelectedMark('')
        setProducts([])
        form.setValue('SK_PRODUTO', '')
        onOpenAddProduct() 
    }

    const { mutate, isPending } = useMutation({
        mutationFn: createResearchedProduct,
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            toast({
                title: 'Produto adicionado na pesquisa'
            })
            onClose()
        },
        onError: () => {
            let errorMessage =
                'Não foi possível adicionar o produto. Tente novamente mais tarde.'

            toast({
                title: 'Erro ao adicionar o produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSave = async (data: z.infer<typeof schema>) => {

            mutate({
                id: 0,
                SK_PESQUISA: MarketResearch.SK_PESQUISA, 
                SK_CLIENTE: data.SK_CLIENTE, 
                SK_PRODUTO: data.SK_PRODUTO, 
                VL_PRECO_COMPRA: data.VL_PRECO_COMPRA,
                QTD_VOLUME_COMPRA: data.QTD_VOLUME_COMPRA,
                VL_PRECO_VENDA: data.VL_PRECO_VENDA,
                DD_LATITUDE: (currentLocation.current.coords.latitude).toString(),
                DD_LONGITUDE: (currentLocation.current.coords.longitude).toString(),  
                IMAGEM: data.IMAGEM?.[0],
                DD_PRODUTO_EXTRA: 1,
                CREATED_AT: new Date().toISOString(),
            })
  
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[600px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Incluir um produto fora da pesquisa</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto max-h-[600px]"
                    >
                        <div className='grid grid-cols-[1fr] gap-2'> 
                             <div
                                className={`fixed top-3 right-3 z-50 px-3 py-1 rounded-full text-xs font-medium
                                    ${online ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                                `}
                                >
                                {online ? "Online" : "Offline"}
                            </div> 
                            <FormField
                                name="SK_CLIENTE"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Cliente*</FormLabel>
                                            {!DataPDV && (
                                            <Input 
                                                disabled
                                                placeholder='Carregamento os clientes'
                                            />
                                        )}
                                        {DataPDV && (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="justify-start w-full gap-2 text-xs"
                                                    >
                                                        {PDV === 0 ? (
                                                            <TypographyXS className="flex-1 text-start">
                                                                Selecione um cliente
                                                            </TypographyXS>
                                                        ) : (
                                                            <>
                                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                                    {DataPDV.find(supp => supp.SK_CLIENTE == PDV)?.DS_NOME_FANTASIA}
                                                                </TypographyXS>
                                                            </>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                                    <VirtualizedCommand
                                                        options={DataPDV.map(item => { return { value: item.SK_CLIENTE.toString(), label: item.DS_NOME_FANTASIA } })}
                                                        placeholder="PDV" 
                                                        selectedValues={{ [`${field.value}`]: true }}
                                                        onSelectionChange={(data) => {
                                                            setPDV(Number(data))
                                                            field.onChange(Number(data)) 
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </Popover>

                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormItem>
                                <FormLabel>Marcas</FormLabel>
                                <Select onValueChange={(value) => setSelectedMark(value)} value={selectedMark}>
                                    <FormControl>
                                        <SelectTrigger>
                                        <SelectValue placeholder="" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {mark.map((item, index) => (
                                            <SelectItem key={index} value={item}>{item}</SelectItem> 
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>

                            <FormField
                                name="SK_PRODUTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Produtos da marca selecionada</FormLabel>
                                        {!products && 
                                            (isFetching ?  
                                                <Input 
                                                    disabled
                                                    placeholder='Carregando produtos'
                                                />
                                              :  
                                                 <Input 
                                                disabled
                                                placeholder='Selecione uma marca'
                                            /> )
                                       }
                                        {products && (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="justify-start w-full gap-2 text-xs"
                                                    >
                                                        {selectedProduct === '' ? (
                                                            <TypographyXS className="flex-1 text-start">
                                                                Selecione um Produto
                                                            </TypographyXS>
                                                        ) : (
                                                            <>
                                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                                    {products.find(supp => supp.SK_PRODUTO == selectedProduct)?.DS_PRODUTO}
                                                                </TypographyXS>
                                                            </>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                                    <VirtualizedCommand
                                                        options={products.map(item => { return { value: item.SK_PRODUTO.toString(), label: item?.DS_PRODUTO } })}
                                                        placeholder="Product" 
                                                        selectedValues={{ [`${field.value}`]: true }}
                                                        onSelectionChange={(data) => {
                                                            setSelectedProduct((data))
                                                            field.onChange((data))
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </Popover>

                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="VL_PRECO_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de compra (Cx)</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="QTD_VOLUME_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Volume em Caixa Semanal (Cx)</FormLabel>
                                        <FormControl>
                                            <Input {...field}  onChange={(e) => field.onChange(parseInt(e.target.value))} type='number'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="VL_PRECO_VENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de venda (un.)</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="IMAGEM"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Imagem do preço do produto</FormLabel>

                                        <FormControl>
                                            <Input
                                            type="file" 
                                            onChange={(e) => {
                                                const files = e.target.files
                                                field.onChange(files) 
                                            }}
                                        />
                                        </FormControl> 
                                        <FormMessage />
                                    </FormItem>
                                )}
                                />
  
                        </div>
                        <div className='py-2'>
                            <TypographyMuted className="text-gray-900">Não encontrou o produto desejado?</TypographyMuted> 
                            <TypographyMuted className="text-black-900 font-semibold cursor-pointer hover:text-primary-500" onClick={() => handleCreateNewProduct()}>
                                Selecione uma marca e clique aqui e cadastre um novo produto
                            </TypographyMuted> 
                        </div>
                                
                        <DialogFooter className='grid grid-cols-2 gap-4'>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="outline"
                            >
                                Cancelar
                            </Button>
                            <Button isLoading={isPending} type="submit">
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
                <AddDialog isOpen={isOpenAddProduct} onClose={onCloseAddProduct} />
            </DialogContent>
        </Dialog>
    )
} 

export default AddNewProductInProductMarketResearchDialog
